import { Dispatch, SetStateAction, useState } from 'react';

import {
  convertBooleanToAvailability,
  convertMmIdToProductCacheKey,
  DOSHII_ITEM_VARIANT_IDENTIFIER,
  listAppsInString,
  useErrorModal,
  setDateToTomorrowAt4AM
} from 'utils';

import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { ProductFragment, SetProductAvailabilityMutation, SnoozeProductOnceAtMutation } from 'queries/Product';
import {
  type App,
  type Product,
  type ProductVariablesByApp,
  type VariantVariablesByApp,
  type Venue,
  Mutation,
  Maybe,
  MenuVariant
} from 'generated/graphql';
import { useModalContext } from 'common/Context/ModalContext';
import { cloneDeep, delay } from 'lodash';
import { SetVariantAvailabilityMutation, MenuVariantFragment } from 'queries/Variant';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { venueHasBrands } from './brand';
import { AvailabilityStatus } from './constants';

const { AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS } = getEnvironmentVariables();

type UseSetAvailabilityResponse = [
  (newAvailability: string, setParentIsLoading?: Dispatch<SetStateAction<boolean>>, updateFn?: () => void) => void,
  boolean
];

export const useSetAvailability = (
  menuItem: Product | MenuVariant | null,
  currentVenueWithMenus: Venue | undefined | null,
  isItemDetails?: boolean
): UseSetAvailabilityResponse => {
  const [setProductAvailability] = useMutation<{
    setProductAvailability: Mutation['setProductAvailability'];
  }>(SetProductAvailabilityMutation);

  const [setVariantAvailability] = useMutation<{
    setVariantAvailability: Mutation['setVariantAvailability'];
  }>(SetVariantAvailabilityMutation);

  const [snoozeProductOnceAt] = useMutation<{
    snoozeProductOnceAt: Mutation['snoozeProductOnceAt'];
  }>(SnoozeProductOnceAtMutation);

  const [availabilityChangeLoading, setAvailabilityChangeLoading] = useState(false);
  const errorModalHandler = useErrorModal();

  const { openModal, openSnackBar } = useModalContext();
  const { cache } = useApolloClient();

  const { __typename: menuItemType, posId } = menuItem || {};

  const handler = (
    availability: string,
    setParentIsLoading?: Dispatch<SetStateAction<boolean>>,
    updateFn?: () => void
  ): void => {
    if (!currentVenueWithMenus || !menuItem || availability === AvailabilityStatus.MIXED) {
      return;
    }
    const isSnoozed = availability === AvailabilityStatus.UNAVAILABLE_TODAY;
    const snoozedUntilDate = isSnoozed ? setDateToTomorrowAt4AM(currentVenueWithMenus.timezone) : null;
    const itemAvailability = isSnoozed ? AvailabilityStatus.UNAVAILABLE : availability;
    const _apps: Set<App> = new Set();
    const variablesByApp: (ProductVariablesByApp | VariantVariablesByApp)[] = [];
    const itemsWithPosId: (Product | MenuVariant)[] = [];
    const allAppsWithPosItem: Set<string> = new Set();
    let wasItemPreviouslySnoozed = false;

    currentVenueWithMenus.menus?.forEach((menu) => {
      const menuItems = menuItemType === 'Product' ? menu.products : menu.variants;
      menuItems.forEach((item) => {
        if (item.posId === posId) {
          menu.apps.forEach((app) => _apps.add(app));
          variablesByApp.push(...item.variablesByApp);
          if (!itemsWithPosId.find((_item) => _item.mmId === item.mmId)) {
            itemsWithPosId.push(item);
          }
          wasItemPreviouslySnoozed = item.variablesByApp.some((appVariable) => appVariable.snoozedUntil);
        }
      });
    });
    // Get all appIds that have the variant with posId
    variablesByApp.map(({ app }) => app.id).forEach((id) => allAppsWithPosItem.add(id));

    const isAvailable = itemAvailability === AvailabilityStatus.AVAILABLE;

    let productAsDoshiiItem: MenuVariant | null = null;
    if (menuItemType === 'Product') {
      // Check if the product has a doshii:item variant
      currentVenueWithMenus.menus?.forEach((menu) => {
        menu.variants.forEach((variant) => {
          if (variant.posId === `${DOSHII_ITEM_VARIANT_IDENTIFIER}${menuItem.posId}`) {
            productAsDoshiiItem = variant;
          }
        });
      });
    }

    const handleSuccess = (): void => {
      openSnackBar(
        `Availability updated. Allow a few minutes for your apps to display.`,
        isItemDetails ? menuItem.mmId : undefined
      );
    };

    const callback = (updateFn?: () => void): void => {
      setAvailabilityChangeLoading(true);
      if (setParentIsLoading) setParentIsLoading(true);

      // Conditionally call the correct function based on `typename`
      let setAvailabilityPromise;
      if (menuItemType === 'Product') {
        const baseVariables = {
          posId,
          locationId: currentVenueWithMenus.hashedId,
          productIsVariantInApps: Array.from(
            new Set(productAsDoshiiItem?.variablesByApp.map((appVariable) => appVariable.app.id))
          )
        };
        setAvailabilityPromise = snoozedUntilDate
          ? snoozeProductOnceAt({
              variables: {
                ...baseVariables,
                onceAt: snoozedUntilDate
              }
            })
          : setProductAvailability({
              variables: {
                ...baseVariables,
                isAvailable,
                wasProductPreviouslySnoozed: wasItemPreviouslySnoozed
              }
            });
      } else if (menuItemType === 'MenuVariant') {
        setAvailabilityPromise = setVariantAvailability({
          variables: {
            posId,
            locationId: currentVenueWithMenus.hashedId,
            appIds: Array.from(allAppsWithPosItem),
            isAvailable
          }
        });
      } else {
        throw new Error('Invalid menu item type');
      }
      setAvailabilityPromise
        .then((response) => {
          const { data, extensions } = response;

          const failedApps: App[] = [];

          let setAvailability;
          if (menuItemType === 'Product') {
            setAvailability = (data as { setProductAvailability?: Maybe<Maybe<Product>[]> })?.setProductAvailability;
          } else if (menuItemType === 'MenuVariant') {
            setAvailability = (data as { setVariantAvailability?: Maybe<Maybe<MenuVariant>[]> })
              ?.setVariantAvailability;
          }

          setAvailability?.forEach((menuItem: Maybe<Product | MenuVariant>) => {
            menuItem?.variablesByApp.forEach((appVariable) => {
              if (
                appVariable.availability !== itemAvailability &&
                !failedApps.find((app) => app.id === appVariable.app.id)
              ) {
                failedApps.push(appVariable.app);
              }
            });
          });

          if (failedApps.length) {
            const error = new ApolloError({
              extraInfo: {
                failedApps,
                subject: `Availability update Failing for ${listAppsInString(
                  failedApps,
                  venueHasBrands(currentVenueWithMenus.menus || []),
                  currentVenueWithMenus.name
                )}`
              },
              graphQLErrors: [
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                {
                  extensions: {
                    mmCode: 6003,
                    requestId: extensions?.requestId
                  }
                }
              ]
            });

            errorModalHandler(error, currentVenueWithMenus);
          } else {
            handleSuccess();
          }
          itemsWithPosId.forEach((item) => {
            const id = cache.identify(item);
            const updatedItem = cloneDeep(item);
            const variablesByApp = updatedItem.variablesByApp.map((appVariable) => {
              return {
                ...appVariable,
                ...(!failedApps.find((app) => app.id === appVariable.app.id) && {
                  availability: convertBooleanToAvailability(isAvailable),
                  snoozedUntil: snoozedUntilDate
                })
              };
            });
            cache.writeFragment({
              id,
              fragment: menuItemType === 'Product' ? ProductFragment : MenuVariantFragment,
              data: { ...updatedItem, variablesByApp },
              fragmentName: menuItemType === 'Product' ? 'ProductFragment' : 'MenuVariantFragment'
            });

            let variantPosId: string | null = null;
            if (menuItemType === 'MenuVariant') {
              variantPosId = item.posId;
            } else if (productAsDoshiiItem) {
              variantPosId = `${DOSHII_ITEM_VARIANT_IDENTIFIER}${item.posId}`;
              const id = cache.identify(productAsDoshiiItem);
              const updatedDoshiiItemVariantOfProduct = cloneDeep(productAsDoshiiItem as MenuVariant);
              updatedDoshiiItemVariantOfProduct.variablesByApp = updatedDoshiiItemVariantOfProduct.variablesByApp.map(
                (appVariable) => {
                  return {
                    ...appVariable,
                    availability: convertBooleanToAvailability(isAvailable)
                  };
                }
              );
              cache.writeFragment({
                id,
                fragment: MenuVariantFragment,
                data: updatedDoshiiItemVariantOfProduct,
                fragmentName: 'MenuVariantFragment'
              });
            }
            if (variantPosId) {
              currentVenueWithMenus?.menus?.forEach((menu) => {
                menu.products.forEach((product) => {
                  let hasVariantChangedForProduct = false;

                  product.options.forEach((option) => {
                    option.variants.forEach((variant) => {
                      if (variant.posId === variantPosId) {
                        hasVariantChangedForProduct = true;
                      }
                    });
                  });
                  if (hasVariantChangedForProduct) {
                    // We get product from cache so we have the updated product.
                    const productFromCache = cache.readFragment(
                      {
                        id: convertMmIdToProductCacheKey(product.mmId),
                        fragment: ProductFragment,
                        fragmentName: 'ProductFragment'
                      },
                      true
                    ) as Product;
                    const updatedProduct = cloneDeep(productFromCache);
                    updatedProduct.options.forEach((option) => {
                      option.variants.forEach((variant) => {
                        if (variant.posId === variantPosId) {
                          variant.variablesByApp = variant.variablesByApp.map((appVariable) => ({
                            ...appVariable,
                            ...(!failedApps.find((app) => app.id === appVariable.app.id) && {
                              availability: convertBooleanToAvailability(isAvailable)
                            })
                          }));
                        }
                      });
                    });
                    const productId = cache.identify(product);
                    cache.writeFragment({
                      id: productId,
                      fragment: ProductFragment,
                      data: updatedProduct,
                      fragmentName: 'ProductFragment'
                    });
                  }
                });
              });
              const unsupportedApps = variablesByApp
                .filter(
                  ({ app }) =>
                    !AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS.includes(
                      Number(app.id.startsWith('virtual') ? app.id.split('.')[1] : app.id)
                    )
                )
                .map(({ app }) => app);
              if (unsupportedApps.length && !isAvailable) {
                openModal({
                  type: 'OPTIONS_AVAILABILITY_UNSUPPORTED_APP_WARNING',
                  apps: unsupportedApps
                });
              }
            }
            if (updateFn) updateFn();
          });
        })
        .catch((error) => {
          errorModalHandler(error, currentVenueWithMenus);
        })
        .finally(() => {
          setAvailabilityChangeLoading(false);
          setParentIsLoading?.(false);
        });
    };

    // const customCallback = async (onSuccess: () => void, onFailure: () => {}) => {};

    const openDoshiiItemConfirmModal = (): void => {
      openModal({
        type: 'DOSHII_ITEM_VARIANT_AVAILABILITY_CHANGE',
        callback: () => callback(updateFn)
      });
    };
    const openAvailabilityConfirmModal = (): void => {
      openModal({
        type: 'AVAILABILITY_CHANGE_CONFIRM',
        apps: Array.from(_apps),
        menuItem,
        callback: () => {
          if (productAsDoshiiItem) {
            delay(openDoshiiItemConfirmModal, 500);
          } else {
            callback(updateFn);
          }
        },
        isAvailable
      });
    };
    if (itemsWithPosId.length > 1) {
      openAvailabilityConfirmModal();
    } else if (productAsDoshiiItem) {
      openDoshiiItemConfirmModal();
    } else {
      callback(updateFn);
    }
  };
  return [handler, availabilityChangeLoading];
};
